import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import { UnitBase } from 'hooks/useDisplayUnits/useDisplayUnits';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import React, { ChangeEvent, FocusEvent, useContext } from 'react';
import {
  createProjectNumberFormatter,
  createProjectNumberParser,
  round,
  sanitiseNumber
} from 'utils';
import { isCustomUnitField } from 'utils/unitConversions';
import ProjectContext from 'modules/project/providers/project/context';

const PREFIX = 'UnitSystemNumberField';

const classes = {
  reducedLabel: `${PREFIX}-reducedLabel`,
};

const StyledInputAdornment = styled(InputAdornment)(() => ({
  [`& .${classes.reducedLabel}`]: {
    '& p.MuiTypography-body1': {
      fontSize: '.7rem',
    },
  },
}));

const getNumberValue = (value: unknown, unitInfo: UnitBase, isCustom: boolean, roundingFn?: (v: number) => number) => {
  const numberValue = sanitiseNumber(value);
  const rounding = roundingFn || ((v: number) => v);

  if (!numberValue) {
    return undefined;
  }

  return isCustom ? rounding(unitInfo.imperial.conversion(value as number)) : rounding(numberValue);
};

interface UnitSystemNumberFieldProps extends Omit<OutlinedTextFieldProps, 'variant'> {
  unitSystem: UnitSystem;
  unitInfo: UnitBase;
  decimalPlaces: number;
  showZero?: boolean;
  customUnitFields?: CustomUnitFields[];
  decimalSeparator?: 0 | 1 | 'Dot' | 'Comma';
  thousandsSeparator?: 0 | 1 | 2 | 'Comma' | 'Dot' | 'Space';
}

const UnitSystemNumberField = (props: UnitSystemNumberFieldProps) => {
  const projectContext = useContext(ProjectContext);
  const {
    value,
    unitSystem,
    unitInfo,
    decimalPlaces,
    showZero,
    customUnitFields,
    decimalSeparator,
    thousandsSeparator,
    onChange,
    onFocus,
    onBlur,
    ...other
  } = props;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields ?? [], unitInfo.customUnitField);
  const effectiveDecimalSeparator = decimalSeparator ?? projectContext?.state.decimalSeparator;
  const effectiveThousandsSeparator = thousandsSeparator ?? projectContext?.state.thousandsSeparator;
  const formatNumber = createProjectNumberFormatter(effectiveDecimalSeparator, effectiveThousandsSeparator);
  const parseNumber = createProjectNumberParser(effectiveDecimalSeparator, effectiveThousandsSeparator);

  const [isFocused, setIsFocused] = React.useState(false);
  const [rawInput, setRawInput] = React.useState<string>('');

  const fallBackDisplay = showZero ? 0 : '';

  const displayValue = getNumberValue(value, unitInfo, isCustom, (number) => round(number, decimalPlaces)) ?? fallBackDisplay;
  const formattedDisplayValue = typeof displayValue === 'number'
    ? formatNumber(displayValue, decimalPlaces)
    : displayValue;

  const inputValue = isFocused ? rawInput : formattedDisplayValue;

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const text = event.target.value;
    setRawInput(text);

    const valueAsNumber = parseNumber(text) ?? sanitiseNumber(text) ?? 0;

    // Metric is the standardised unit so always return in metric
    const newValue = isCustom ? unitInfo.metric.conversion(valueAsNumber) : valueAsNumber;

    onChange && onChange({
      ...event,
      target: {
        ...event.target,
        value: newValue.toString(),
      },
    });
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRawInput(typeof formattedDisplayValue === 'string' ? formattedDisplayValue : `${formattedDisplayValue}`);
    setIsFocused(true);
    onFocus && onFocus(event as FocusEvent<HTMLInputElement>);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur && onBlur(event as FocusEvent<HTMLInputElement>);
  };

  return (
    <TextField
      {...other}
      value={inputValue}
      onChange={handleOnChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      type="text"
      color="secondary"
      fullWidth
      variant="outlined"
      className={classes.reducedLabel}
      slotProps={{
        htmlInput: {
          inputMode: 'decimal',
        },
        input: {
          endAdornment: (
            <StyledInputAdornment position="end">
              {isCustom ? unitInfo.imperial.abbreviation : unitInfo.metric.abbreviation}
            </StyledInputAdornment>
          ),
        },
      }}
    />
  );
};

export default UnitSystemNumberField;
